import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

interface OnboardingStepperProps {
  /** The user's current step (1-indexed). Steps ≤ `step` show as completed
   *  (check icon, primary bg); steps > `step` show as pending (number, track bg).
   *  Connector segments fill independently per pair of dots so step=2 renders
   *  segment 1 blue + segment 2 gray (matches Figma `Add Your Organization
   *  Details` screen). */
  step: 1 | 2 | 3;
  className?: string;
}

// Two connector segments instead of one absolute fill bar so each
// transition (dot1→dot2, dot2→dot3) can be coloured independently:
//   step=1 -> seg1 gray,  seg2 gray   (only dot 1 active)
//   step=2 -> seg1 blue,  seg2 gray   (dots 1+2 active)
//   step=3 -> seg1 blue,  seg2 blue   (all dots active; celebratory)
export function OnboardingStepper({ step, className }: OnboardingStepperProps) {
  return (
    <div className={cn('max-w-[326px] w-full py-4 mx-auto max-lg:mt-5', className)}>
      <div className="bg-track h-1 flex items-center justify-between relative">
        <span className={cn('absolute top-0 h-full z-0 left-0 w-1/2', step >= 2 && 'bg-primary')} />
        <span
          className={cn('absolute top-0 h-full z-0 left-1/2 w-1/2', step >= 3 && 'bg-primary')}
        />
        {[1, 2, 3].map((n) => {
          const done = n <= step;
          return (
            <div key={n} className="h-1 flex items-center relative z-2">
              <div
                className={cn(
                  'h-7 w-7 rounded-full flex items-center justify-center',
                  done ? 'bg-primary' : 'bg-track',
                )}
              >
                {done ? (
                  <Check className="w-5 h-5 text-white" />
                ) : (
                  <span className="text-lg leading-[150%] font-medium text-disabled">{n}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
